import Link from 'next/link';
import React, { useCallback, useState } from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import styled from '@emotion/styled';
import { Button } from '@mui/material';
import MuiMenu, { menuClasses } from '@mui/material/Menu';
import MuiMenuItem, { menuItemClasses } from '@mui/material/MenuItem';
import { useTranslations } from 'next-intl';
import { useBoolean } from 'usehooks-ts';

import { PERSONA_NFT_STORAGE } from '@web/features/const';
import { useBridgeManager } from '@web/features/staking/BridgeManager';
import { IStakedDeposit } from '@web/features/staking/hooks';
import { ClaimModal } from '@web/features/staking/modals/ClaimModal';
import { UnstakeModal } from '@web/features/staking/modals/UnstakeModal';
import {
  BridgeIcon,
  ClaimIcon,
  DownloadActionIcon,
  MoreActionIcon,
  UnstakeIcon,
} from '@web/ui/icons';

const MoreTrigger = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  right: ${({ theme }) => theme.spacing('0500')};
  top: ${({ theme }) => theme.spacing('0500')};
  border-radius: ${({ theme }) => theme.shape.corner['0500']};
  background: ${({ theme }) => theme.color.surfaceContainer};
  width: ${({ theme }) => theme.size[2000]};
  height: ${({ theme }) => theme.size[2000]};
  min-width: unset;
  z-index: 3;

  :hover {
    background: ${({ theme }) => theme.color.surfaceContainerLowest};
  }

  :active {
    border-radius: ${({ theme }) => theme.shape.corner['0750']};
  }
`;

const Menu = styled(MuiMenu)`
  .${menuClasses.paper} {
    position: absolute;
    margin-top: ${({ theme }) => theme.spacing('1000')};
    border-radius: ${({ theme }) => theme.shape.corner[1000]};
    box-shadow: ${({ theme }) => theme.elevation.level1};
    background: ${({ theme }) => theme.color.surfaceContainer};
  }
`;

const MenuItem = styled(MuiMenuItem)`
  &.${menuItemClasses.root} {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: ${({ theme }) => theme.spacing('0500')};
    padding-inline: ${({ theme }) => theme.spacing('1000')};
    padding-block: ${({ theme }) => theme.spacing('0750')};
    width: 100%;
  }
`;

const ActionName = styled.p`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
`;

const DownloadLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
`;

const MenuItems = styled.div``;

interface IGridItemMenuProps {
  disabled: boolean;
  persona: IPersonaFront;
  deposit: IStakedDeposit | null;
  className: string;
  onMenuOpen: (isMenuOpen: boolean) => void;
}

export const GridItemMenu = ({
  persona,
  deposit,
  className,
  disabled,
  onMenuOpen,
}: IGridItemMenuProps) => {
  const t = useTranslations();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { createInstance } = useBridgeManager();

  const {
    value: isClaimModalOpen,
    setTrue: openClaimModal,
    setFalse: closeClaimModal,
  } = useBoolean(false);

  const {
    value: isUnstakeModalOpen,
    setTrue: openUnstakeModal,
    setFalse: closeUnstakeModal,
  } = useBoolean(false);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    onMenuOpen(true);
  };

  const handleClose = () => {
    onMenuOpen(false);
    setAnchorEl(null);
  };

  const handleBridge = useCallback(() => {
    createInstance({
      tokenIds: [persona.tokenId],
      source: persona.blockchain,
    });
  }, [persona]);

  return (
    <>
      <MoreTrigger onClick={handleOpen} className={className}>
        <MoreActionIcon color="onBackground" />
      </MoreTrigger>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        onMouseLeave={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        disableEnforceFocus
      >
        <MenuItems>
          {deposit ? (
            <>
              <MenuItem onClick={openClaimModal} disabled={disabled}>
                <ClaimIcon color="onSurface" />
                <ActionName>{t('BULK_ACTION_TOOLTIP_CLAIM')}</ActionName>
              </MenuItem>
              <MenuItem onClick={openUnstakeModal} disabled={disabled}>
                <UnstakeIcon color="onSurface" />
                <ActionName>{t('BULK_ACTION_TOOLTIP_UNSTAKE')}</ActionName>
              </MenuItem>
            </>
          ) : (
            <MenuItems>
              <MenuItem onClick={handleBridge} disabled={disabled}>
                <BridgeIcon color="onSurface" />
                <ActionName>{t('BULK_ACTION_TOOLTIP_BRIDGE')}</ActionName>
              </MenuItem>
            </MenuItems>
          )}
          <MenuItem>
            <DownloadLink
              href={`${PERSONA_NFT_STORAGE}/${persona.tokenId}/4096.jpeg`}
              download
            >
              <DownloadActionIcon color="onSurface" />
              <ActionName>{t('STAKING_GRID_ITEM_MENU_DOWNLOAD')}</ActionName>
            </DownloadLink>
          </MenuItem>
        </MenuItems>
      </Menu>

      <ClaimModal
        personas={[persona]}
        open={isClaimModalOpen}
        onClose={closeClaimModal}
      />
      <UnstakeModal
        personas={[persona]}
        open={isUnstakeModalOpen}
        onClose={closeUnstakeModal}
      />
    </>
  );
};
